import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import includes from 'lodash/includes';
import Add3Icon from 'zignal-landscape/lib/Icon/Add3Icon';

import styles from './SelectView.css';
import CategoryItem from '../CategoryItem.js';
import FreeformForm from './FreeformForm.js';
import FileUploadForm from './FileUploadForm.js';
import IframeForm from './IframeForm.js';

const EDIT_FORMS = {
  freeform: FreeformForm,
  pdf: FileUploadForm,
  swf: FileUploadForm,
  iframe: IframeForm
};

const SelectView = ({
  createCustomWidget,
  decrement,
  editWidget,
  deleteCustomWidget,
  fileUploadUrl,
  increment,
  itemKeyForEditing,
  selectForEditing,
  selectListItem,
  selectedListItem,
  widgets,
  widgetIdsOnDashboard,
  handleSuccess = noop,
  focusSearchInput = noop
}) => {

  function saveWidget(widget) {
    const transformedData = Object.assign({
      modifiedAt: Date.now(),
      name: widget.title // for Custom widgets, name and title are the same
    }, widget);
    editWidget(transformedData);
    selectForEditing(null); // close editing form
  }

  function renderEditForm(item) {
    const subtype = item.subtype;
    const FormComponent = EDIT_FORMS[subtype];

    // for Custom widgets, name and title are the same
    item.title = item.title || item.name;

    return (
      <section className={styles.editFormWrapper}>
        <FormComponent
          data={item}
          onCancel={() => { selectForEditing(null); }}
          onSubmit={saveWidget}
          fileExtension={subtype.toLowerCase()}
          fileUploadUrl={fileUploadUrl}
          isEditing
        />
      </section>
    );
  }

  function customIncrement(item) {
    // prevent multiple instances of custom widgets
    if(item.count < 1) {
      increment(item);
    }
  }

  function isDisabled(item) {
    // Check if widget is already on dashboard; only allow 1 instance
    return (
      includes(widgetIdsOnDashboard, item.id) ||
      item.count > 0
    );
  }

  function renderWidgetList(customSubtype) {
    const filteredWidgets = widgets.filter((widget) => widget.subtype === customSubtype);

    const categoryItemsKeys = () => {
      return filteredWidgets.map((widget) => {
        return  `${customSubtype}${widget.id}`;
      });
    };
    const navigateUp = () => {
      const currentItemIndex = categoryItemsKeys().indexOf(selectedListItem);
      const nextItemIndex = currentItemIndex - 1 > -1 ? currentItemIndex -1: currentItemIndex;
      const nextItem = filteredWidgets[nextItemIndex];
      const nextItemKey = `${customSubtype}${nextItem.id}`;
      if (nextItemIndex === currentItemIndex) {
        focusSearchInput();
      }
      selectListItem(nextItemKey);
    };
    const navigateDown = () => {
      const currentItemIndex = categoryItemsKeys().indexOf(selectedListItem);
      const nextItemIndex = currentItemIndex + 1 < filteredWidgets.length ? currentItemIndex +1: currentItemIndex;
      const nextItem = filteredWidgets[nextItemIndex];
      const nextItemKey = `${customSubtype}${nextItem.id}`;
      selectListItem(nextItemKey);
    };
    const handleSuccessOnEnter = () => {
      const currentItemIndex = categoryItemsKeys().indexOf(selectedListItem);
      const currentItem = filteredWidgets[currentItemIndex];
      selectListItem(selectedListItem, currentItem);
      customIncrement(currentItem);
      handleSuccess();
    };

    const categoryItems = filteredWidgets.map((widget) => {
      const itemKey = `${customSubtype}${widget.id}`;

      let description = '';
      if(widget.createdAt) {
        const createdAtString = new Date(widget.createdAt * 1000).toLocaleDateString();
        description += `Created ${createdAtString}`;
      }
      if(widget.createdAt && widget.modifiedAt) {
        description += ', ';
      }
      if(widget.modifiedAt) {
        const modifiedAtString = new Date(widget.modifiedAt * 1000).toLocaleDateString();
        description += `Edited ${modifiedAtString}`;
      }
      // TODO: Avoid mutation
      const item = Object.assign(widget, { description, name: widget.name });
      const editForm = (itemKeyForEditing === itemKey) ? renderEditForm(widget) : null;

      return (
        <li key={itemKey}>
          <CategoryItem
            key={itemKey}
            item={item}
            onClick={() => selectListItem(itemKey)}
            increment={() => customIncrement(item)}
            decrement={() => decrement(item)}
            selected={selectedListItem === itemKey}
            toggleEdit={() => selectForEditing(itemKey)}
            disabled={isDisabled(item)}
            deleteItem={() => deleteCustomWidget(item)}
            isEditable
            showDescription
            tabIndex="0"
            navigateUp={navigateUp}
            navigateDown={navigateDown}
            onEnterPress={handleSuccessOnEnter}
          />

          {editForm}
        </li>
      );
    });

    const noItemsMessage = (
      <li className={styles.noItemsMessage}>
        No widgets of this type have been created yet.
      </li>
    );

    const prettySubtype = (customSubtype === 'pdf' || customSubtype === 'swf')
      ? customSubtype
      : customSubtype.toUpperCase();

    return (
      <section>
        <h4 className={styles.title}>{prettySubtype}</h4>
        <ul className={styles.widgetList}>
          {categoryItems.length > 0 ? categoryItems : noItemsMessage}
        </ul>
      </section>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Create New widget */}
      <section className={styles.creationWrapper} onClick={createCustomWidget}>
        <Add3Icon />
        <h4 className={styles.creationTitle}>Create new custom widget</h4>
        <p className={styles.description}>Freeform, PDF, SWF, iframe</p>
      </section>

      {/* available widgets */}
      {renderWidgetList('freeform')}
      {renderWidgetList('pdf')}
      {renderWidgetList('swf')}
      {renderWidgetList('iframe')}
    </div>
  );
};

SelectView.propTypes = {
  createCustomWidget: PropTypes.func.isRequired,
  decrement: PropTypes.func,
  deleteCustomWidget: PropTypes.func.isRequired,
  editWidget: PropTypes.func,
  handleSuccess: PropTypes.func,
  focusSearchInput: PropTypes.func,
  fileUploadUrl: PropTypes.string,
  increment: PropTypes.func,
  itemKeyForEditing: PropTypes.string,
  selectForEditing: PropTypes.func,
  selectListItem: PropTypes.func,
  selectedListItem: PropTypes.string,
  widgets: PropTypes.arrayOf(PropTypes.object).isRequired,
  widgetIdsOnDashboard: PropTypes.arrayOf(PropTypes.string)
};

export default SelectView;
